import { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';

export interface IntegrationCardProps {
  /** Small square logo/initial shown before the name. */
  icon: ReactNode;
  name: string;
  /** One-line plain explanation of what connecting does. */
  description: string;
  isConnected: boolean;
  /** True while the connection state is still being read. */
  checking?: boolean;
  isConnecting?: boolean;
  lastSyncedAt?: string | null;
  onConnect: () => void;
  onDisconnect: () => void;
  /** Buttons shown once connected (loading data, running a workflow, …). */
  actions?: ReactNode;
  /** Records/files preview rendered under the actions. */
  children?: ReactNode;
}

/**
 * One shared card for every connected app, so HubSpot, Zoho, Dropbox and
 * OneDrive look and behave identically.
 */
export const IntegrationCard = ({
  icon,
  name,
  description,
  isConnected,
  checking = false,
  isConnecting = false,
  lastSyncedAt,
  onConnect,
  onDisconnect,
  actions,
  children,
}: IntegrationCardProps) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          {name}
          {isConnected && (
            <Badge variant="default" className="bg-green-100 text-green-800">
              <CheckCircle className="w-3 h-3 mr-1" />
              Connected
            </Badge>
          )}
        </div>
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{description}</AlertDescription>
        </Alert>

        {checking ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your {name} connection…
          </div>
        ) : !isConnected ? (
          <Button onClick={onConnect} disabled={isConnecting} className="w-full">
            {isConnecting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Opening {name}…</>
            ) : (
              <><ExternalLink className="w-4 h-4 mr-2" />Sign in with {name}</>
            )}
          </Button>
        ) : (
          <div className="space-y-4">
            {lastSyncedAt && (
              <p className="text-xs text-muted-foreground">
                Last synced {format(new Date(lastSyncedAt), 'MMM d, yyyy · h:mm a')}
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
              {actions}
              <Button onClick={onDisconnect} variant="outline">Disconnect</Button>
            </div>
            {children}
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);
